import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { DataList, MessageModal, TabNavigation, noRowsDefault } from 'common/components';
import EntityDetailsPopup from 'common/components/custom/entity-details-popup/EntityDetailsPopup';
import {
    VARIANT_LIST,
    DELETE_POPUP_TITLE,
    DELETE_POPUP_SUMMARY_PART1,
    DELETE_POPUP_SUMMARY_PART2,
    RESTORE_CONFIRMATION_TITLE,
    RESTORE_CONFIRMATION_TEXT_MASTER,
    RESTORE_CONFIRMATION_TEXT_VARIANT,
    MAIN_ROUTES,
    FAILURE_HEADER,
    ARCHIVE_DELETE_DISABLED
} from 'common/config';
import { ApiStatus, ButtonLabel, MessageType, UploadStatus, Variant } from 'common/enums';
import { Action, EntityActions } from 'common/enums/actions.enum';
import { ApiInitiator, EntityType } from 'common/enums/data.enum';
import { EntityDetailsPayload } from 'common/models/data/entityStore.model';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { EntityVM } from 'common/models/data/entity.model';
import { RootState } from 'common/redux/core/root.reducer';
import { getMessageModalDetails } from 'common/redux/app-state/appState.selector';
import { getUploadStatus } from 'common/redux/file-upload/fileUpload.selector';
import { appStateActions } from 'common/redux/app-state';

import Header from 'modules/archive/components/header/Header';
import EntityRestore from 'modules/archive/components/entity-restore/EntityRestore';
import ArchiveSearchResultList from 'modules/archive/components/archive-search-result-list/ArchiveSearchResultList';
import { getArchiveColDefs } from 'modules/archive/config';
import { archiveActions } from 'modules/archive/redux';
import { masterDataActions } from 'modules/master-data/redux';
import {
    getArchiveList,
    getArchiveListPagination,
    getArchiveListStatus,
    getHierarchicalDataResponse,
    getRestoreDataApiStatus,
    getRestoredData,
    getReviewStatus
} from 'modules/archive/redux/archive.selector';
import useArchiveAccess from 'modules/archive/role-access-hooks/useArchiveAccess';

import './ArchiveList.scss';
import { RestoreDataDTO } from 'modules/archive/models/archive.model';

const ArchiveList: FC = () => {
    const [isShowDetailsPopup, setShowDetailsPopup] = useState<boolean>(false);
    const [modalData, setModalData] = useState<any>(null);
    const [selectedVariant, setSelectedVariant] = useState<string>(VARIANT_LIST[0]);
    const [searchVal, setSearchVal] = useState<string>('');

    const [isShowPopup, setShowPopup] = useState<boolean>(false);
    const [popupDetails, setPopupDetails] = useState({ title: '', text: '', type: '' });
    const [entityDetailsPayload, setEntityDetailsPayload] = useState<EntityDetailsPayload>();
    const [isRestoreModalVisible, setIsRestoreModalVisible] = useState<boolean>(false);
    const [pagedRequest, setPagedRequest] = useState<PagedRequest>({
        pageNumber: 1,
        pageSize: 20,
        searchText: ''
    });
    const [hierarchyPageNumber, setHierarchyPageNumber] = useState<number>(1);

    const archiveList = useSelector((state: RootState) => getArchiveList(state));
    const archiveListStatus = useSelector((state: RootState) => getArchiveListStatus(state));
    const archiveListPagination = useSelector((state: RootState) =>
        getArchiveListPagination(state)
    );
    const dataToBeRestored = useSelector((state: RootState) => getRestoredData(state));
    const messageModal = useSelector((state: RootState) => getMessageModalDetails(state));
    const restoreDataApiStatus = useSelector((state: RootState) => getRestoreDataApiStatus(state));

    const dataEditorUploadStatus = useSelector((state: RootState) => getUploadStatus(state));
    const reviewStatus = useSelector((state: RootState) => getReviewStatus(state));
    const hierarchicalDataResponse = useSelector((state: RootState) =>
        getHierarchicalDataResponse(state)
    );
    const { isRestoreArchivedDisabled, isDeleteArchivedDisabled } = useArchiveAccess();
    const uploadedUser = dataEditorUploadStatus?.data?.fileDetails?.uploadedUser?.displayName;
    const isUploadDisabled =
        dataEditorUploadStatus?.data?.previewStatus === UploadStatus.InProgress ||
        dataEditorUploadStatus?.data?.previewStatus === UploadStatus.Validation;

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const fetchHierarchyData = useCallback(
        (pageNo?: number, data?: any) => {
            const pagedRequest = {
                pageNumber: pageNo ?? 1,
                pageSize: 20,
                searchText: ''
            };

            const entityId = data?.id;
            const entityType = data?.entityType;
            dispatch(
                archiveActions.getHierarchicalData({
                    pagedRequest: pagedRequest,
                    entityId: entityId,
                    type: entityType
                })
            );
        },
        [dispatch]
    );

    const getVariantRoute = (variant: string) => {
        if (variant == Variant.MasterData) {
            return MAIN_ROUTES.MASTER_DATA;
        } else if (variant === Variant.GbbPublic) {
            return MAIN_ROUTES.VARIANT_GBB_PUBLIC;
        }
        return '';
    };

    useEffect(() => {
        dispatch(masterDataActions.resetReviewStatus());
    }, [dispatch]);

    useEffect(() => {
        dispatch(archiveActions.fetchArchiveList({ variant: selectedVariant, pagedRequest }));
    }, [dispatch, selectedVariant, pagedRequest]);

    useEffect(() => {
        if (!messageModal?.isVisible && restoreDataApiStatus === ApiStatus.SUCCESS) {
            navigate(
                getVariantRoute(
                    dataToBeRestored.variant ? dataToBeRestored.variant : Variant.MasterData
                )
            );
            dispatch(archiveActions.resetState());
        }
    }, [
        dispatch,
        isShowPopup,
        messageModal?.isVisible,
        navigate,
        dataToBeRestored.variant,
        restoreDataApiStatus
    ]);

    useEffect(() => {
        if (
            reviewStatus?.status === ApiStatus.SUCCESS &&
            modalData?.entityAction === EntityActions.Delete
        ) {
            if (!reviewStatus.data) {
                setPopupDetails({
                    title: DELETE_POPUP_TITLE,
                    text: `${DELETE_POPUP_SUMMARY_PART1} this record. ${DELETE_POPUP_SUMMARY_PART2}`,
                    type: EntityActions.Delete
                });
                setShowPopup(true);
            } else {
                setModalData(undefined);
                dispatch(
                    appStateActions.showMessageModal({
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: ARCHIVE_DELETE_DISABLED,
                        type: MessageType.Error
                    })
                );
            }
            dispatch(masterDataActions.resetReviewStatus());
        }
    }, [reviewStatus, dispatch, modalData?.entityAction]);

    const onSearchChangeHandler = (event: any) => {
        setSearchVal(event.target.value);
    };

    useEffect(() => {
        if (searchVal.length < 3) {
            dispatch(archiveActions.fetchArchiveList({ variant: selectedVariant, pagedRequest }));
        }
    }, [searchVal]);

    useEffect(() => {
        fetchHierarchyData(hierarchyPageNumber);
    }, [dispatch, fetchHierarchyData, hierarchyPageNumber]);

    const handleDetailsClick = (rowData: EntityVM) => {
        setShowDetailsPopup(true);
        setEntityDetailsPayload({
            entityType: rowData.entityType,
            id: rowData.id,
            initiator: ApiInitiator.ArchiveList,
            parentSubCategoryId: rowData.hierarchyId
        });
    };

    const onDelete = (data: any) => {
        dispatch(masterDataActions.fetchReviewStatus());
        data = { ...data, entityAction: EntityActions.Delete };
        setModalData(data);
    };

    const handleExpansionToggle = (rowData: any) => {
        dispatch(archiveActions.toggleExpansion(rowData));
    };

    const onRestore = (data: any) => {
        if (data.entityType === EntityType.Category) {
            const modalText =
                data.variant === Variant.MasterData
                    ? RESTORE_CONFIRMATION_TEXT_MASTER
                    : `${RESTORE_CONFIRMATION_TEXT_VARIANT}${Variant.MasterData}`;
            setPopupDetails({
                title: RESTORE_CONFIRMATION_TITLE,
                text: modalText,
                type: EntityActions.Restore
            });
            setModalData(data);
            setShowPopup(true);
        } else {
            fetchHierarchyData(hierarchyPageNumber, data);
            setIsRestoreModalVisible(true);
        }
        setModalData(data);
        dispatch(archiveActions.setRestoredData(data));
    };

    const handleAction = (action: any, data: any) => {
        switch (action) {
            case EntityActions.Delete:
            case Action.Delete: {
                onDelete(data);
                break;
            }
            case EntityActions.Restore: {
                onRestore(data);
                break;
            }
            default:
                break;
        }
    };

    const handleVariantChange = (value: string) => {
        setSelectedVariant(value);
    };

    const handleMessageConfirmation = () => {
        if (popupDetails.type == EntityActions.Delete) {
            dispatch(
                archiveActions.deleteArchiveDataBegin({
                    entityId: modalData.id,
                    entityType: modalData.entityType
                })
            );
        } else if (popupDetails.type === EntityActions.Restore) {
            dispatch(archiveActions.restore(dataToBeRestored));
            setModalData(null);
        }
        setShowPopup(false);
    };

    const updateSelectedRadioButton = (entity: EntityVM) => {
        dispatch(
            archiveActions.setRadioButtonSelected({
                targetId: entity.id
            })
        );
    };

    const handleRestoreClose = () => {
        setIsRestoreModalVisible(false);
        setModalData(null);
        setHierarchyPageNumber(1);
        dispatch(archiveActions.resetHierarchicalData());
    };

    const handleRestore = (data: any) => {
        // applicable only for sub-cat and indicator
        setIsRestoreModalVisible(false);
        const modalText =
            data.variant === Variant.MasterData
                ? RESTORE_CONFIRMATION_TEXT_MASTER
                : `${RESTORE_CONFIRMATION_TEXT_VARIANT}${Variant.MasterData}`;
        setPopupDetails({
            title: RESTORE_CONFIRMATION_TITLE,
            text: modalText,
            type: EntityActions.Restore
        });
        setShowPopup(true);
        dispatch(archiveActions.resetHierarchicalData());
        dispatch(archiveActions.setRestoredData(data));
    };

    const handleMessageClose = () => {
        setShowPopup(false);
        if (
            popupDetails.type === EntityActions.Restore &&
            modalData.entityType !== EntityType.Category
        ) {
            setIsRestoreModalVisible(true);
        }
    };

    /**
     * To handle infinite scroll
     */
    const handleScroll = () => {
        if (searchVal == '' || searchVal.length < 3) {
            if (
                archiveListStatus !== ApiStatus.LOADING &&
                archiveListPagination &&
                archiveListPagination.totalPages > pagedRequest.pageNumber
            ) {
                const updatedPagedRequest: PagedRequest = { ...pagedRequest };
                updatedPagedRequest.pageNumber = updatedPagedRequest.pageNumber + 1;
                setPagedRequest(updatedPagedRequest);
                dispatch(
                    archiveActions.fetchArchiveList({
                        variant: selectedVariant,
                        pagedRequest: updatedPagedRequest
                    })
                );
            }
        }
    };

    const handleHierarchicalDataToggle = (data: EntityVM) =>
        dispatch(archiveActions.toggleHierarchicalDataExpansion(data));

    const fetchNextHierachySet = () => {
        if (
            hierarchicalDataResponse.status !== ApiStatus.LOADING &&
            hierarchicalDataResponse.data?.pagination &&
            hierarchicalDataResponse.data?.pagination.totalPages > hierarchyPageNumber
        ) {
            setHierarchyPageNumber(hierarchyPageNumber + 1);
        }
    };

    return (
        <div className="archive-container">
            <Header searchVal={searchVal} onSearchChangeHandler={onSearchChangeHandler} />
            <div className="archive-container__archive-list-tabs">
                <TabNavigation
                    tabs={VARIANT_LIST}
                    className="archive-container__archive-list-tabs__tab-navigation"
                    onTabClick={handleVariantChange}
                />
            </div>
            <div className="archive-list-container">
                {searchVal.length >= 3 ? (
                    <ArchiveSearchResultList
                        searchVal={searchVal}
                        isUploadDisabled={isUploadDisabled}
                        uploadedUser={uploadedUser ?? ''}
                        handleAction={handleAction}
                        handleDetailsClick={handleDetailsClick}
                        isRestoreActionDisabled={isRestoreArchivedDisabled}
                        isDeleteActionDisabled={isDeleteArchivedDisabled}></ArchiveSearchResultList>
                ) : (
                    <DataList
                        isNoDataVisible={archiveList.length === 0}
                        data={archiveList}
                        noRowsTemplate={noRowsDefault}
                        fetchNextDataSet={handleScroll}
                        columnDefs={getArchiveColDefs(
                            handleExpansionToggle,
                            handleDetailsClick,
                            handleAction,
                            isRestoreArchivedDisabled,
                            isDeleteArchivedDisabled
                        )}
                    />
                )}
            </div>
            {entityDetailsPayload && (
                <EntityDetailsPopup
                    isEditHidden
                    isShowDetailsPopup={isShowDetailsPopup}
                    entity={entityDetailsPayload}
                    closeHandler={() => setShowDetailsPopup(false)}
                />
            )}
            {modalData && (
                <EntityRestore
                    isVisible={isRestoreModalVisible}
                    entityId={modalData.parentId}
                    data={modalData}
                    parentSubCategory={modalData}
                    variant={selectedVariant}
                    toggleExpansion={handleHierarchicalDataToggle}
                    updateSelectedRadioButton={updateSelectedRadioButton}
                    onSubmit={handleRestore}
                    onClose={handleRestoreClose}
                    fetchNextHierachySet={fetchNextHierachySet}
                />
            )}
            <MessageModal
                headerName={popupDetails.title}
                message={popupDetails.text}
                isVisible={isShowPopup}
                isSecondaryButtonVisible
                type={MessageType.Warning}
                primaryButton={ButtonLabel.Ok}
                secondaryButton={ButtonLabel.Cancel}
                onPrimaryAction={handleMessageConfirmation}
                onSecondaryAction={handleMessageClose}
            />
        </div>
    );
};

export default ArchiveList;
